import React, { useRef, useCallback, useEffect } from 'react';
import styles from './LabelsCell.less';

import { labelByColor } from 'app/gamma/src/util/sort';
import { HasNonNullKeys } from 'app/gamma/src/util/types';
import { LabelModel } from 'app/gamma/src/types/models';
import { CanonicalCard } from '@atlassian/trello-canonical-components';
import { Popover, usePopover, PopoverScreen } from '@trello/nachos/popover';
import { forTemplate } from '@trello/i18n';

import {
  CardLabel,
  LabelColor,
  labelColorToColorBlindPattern,
} from '@atlassian/trello-canonical-components/src/card-front/CardLabel';
import { BoardTableCardLabelSelectView } from 'app/scripts/views/label/board-table-card-label-select-view';
import PopOver from 'app/scripts/views/lib/pop-over';

import { CellWrapper } from './CellWrapper';
import { Analytics } from '@trello/atlassian-analytics';
import { TableTestClasses } from '@trello/test-ids';

const { CardLabels } = CanonicalCard;

const format = forTemplate('multi_board_table_view');

enum Screen {
  CurrentLabelsScreen,
}

interface LabelsCellProps {
  idCard: string;
  idBoard: string;
  idOrg: string;
  labels: LabelModel[];
  colorBlind: boolean;
  refetchBoardTableData: () => void;
  editable?: boolean;
}

export const LabelsCell: React.FunctionComponent<LabelsCellProps> = React.memo(
  ({
    idBoard,
    idCard,
    idOrg,
    labels,
    colorBlind,
    refetchBoardTableData,
    editable,
  }) => {
    const { toggle, triggerRef, popoverProps } = usePopover<HTMLDivElement>({
      initialScreen: Screen.CurrentLabelsScreen,
    });

    const classicTriggerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (popoverProps.isVisible) {
        Analytics.sendScreenEvent({
          name: 'cardLabelsListInlineDialog',
          containers: {
            organization: {
              id: idOrg,
            },
          },
        });
      }
    }, [idOrg, popoverProps.isVisible]);

    const openLabelSelectPopover = useCallback(() => {
      if (editable) {
        if (!PopOver.isVisible) {
          Analytics.sendClickedButtonEvent({
            buttonName: 'changeLabelsButton',
            source: 'multiBoardTableViewScreen',
            containers: {
              organization: {
                id: idOrg,
              },
            },
          });
        }

        PopOver.toggle({
          elem: classicTriggerRef.current,
          view: BoardTableCardLabelSelectView,
          options: {
            idCard,
            idBoard,
            onEditSubmit: refetchBoardTableData,
          },
        });
      } else {
        if (!popoverProps.isVisible) {
          Analytics.sendClickedButtonEvent({
            buttonName: 'labelsListButton',
            source: 'multiBoardTableViewScreen',
            containers: {
              organization: {
                id: idOrg,
              },
            },
          });
        }

        toggle();
      }
    }, [
      idCard,
      idBoard,
      idOrg,
      classicTriggerRef,
      refetchBoardTableData,
      toggle,
      editable,
      popoverProps.isVisible,
    ]);

    const items = [...labels].sort(labelByColor);
    const isEmpty = labels.length === 0;
    const emptyReadOnly = !editable && isEmpty;

    return (
      <>
        <CellWrapper
          className={styles.cardLabelsWrapper}
          role="button"
          onClick={openLabelSelectPopover}
          ref={editable ? classicTriggerRef : triggerRef}
          isEmpty={isEmpty}
          clickable={!emptyReadOnly ? true : false}
        >
          <CardLabels className={styles.cardLabels}>
            {items.map((label: HasNonNullKeys<LabelModel, 'color'>) => (
              <CardLabel
                className={styles.cardLabelOverrides}
                dataTestClass={`${label.color}-${TableTestClasses.LabelBadge}`}
                key={label.id}
                color={(label.color || 'gray') as LabelColor}
                pattern={
                  colorBlind
                    ? labelColorToColorBlindPattern[
                        (label.color || 'gray') as LabelColor
                      ]
                    : null
                }
              >
                {label.name || ' '}
              </CardLabel>
            ))}
          </CardLabels>
        </CellWrapper>

        {!editable && !isEmpty && (
          <Popover {...popoverProps} title={format('labels')}>
            <PopoverScreen id={Screen.CurrentLabelsScreen}>
              {items.map((label: HasNonNullKeys<LabelModel, 'color'>) => (
                <CardLabel
                  className={styles.cardLabelReadOnly}
                  key={label.id}
                  color={(label.color || 'gray') as LabelColor}
                  pattern={
                    colorBlind
                      ? labelColorToColorBlindPattern[
                          (label.color || 'gray') as LabelColor
                        ]
                      : null
                  }
                >
                  {label.name || ' '}
                </CardLabel>
              ))}
            </PopoverScreen>
          </Popover>
        )}
      </>
    );
  },
);
